import { Spacing } from '@persona-id/design-tokens';
import { isEqual, lowerCase, startCase, upperFirst } from 'lodash/fp';
import styled, { css } from 'styled-components';

import { matchLevelSatisfies } from 'dashboard/lib/utils/matchLevel';
import { ExtractionType, MatchLevel } from 'dashboard/models/document';
import {
  ExtractionGroup,
  FocusedExtraction,
} from 'dashboard/redux/reducers/document-viewer-document-generic';
import { ExtractionColor } from 'dashboard/views/DashboardDocumentViewerView/ExtractionHighlights';
import ExtractionToggle from 'dashboard/views/DashboardDocumentViewerView/ExtractionToggle';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import { Paragraph } from 'design-system/components/Text';

const Container = styled.div<{ focused?: boolean }>`
  ${({ onClick }) =>
    onClick &&
    css`
      cursor: pointer;
    `}
  ${({ focused, theme }) =>
    focused &&
    css`
      background: ${theme.color.background.secondary};
    `}
  padding: ${Spacing[3]} ${Spacing[5]};
  border-top: ${({ theme }) => `1px ${theme.color.border.secondary} solid`};
  border-radius: 0 ${({ theme }) => theme.element.borderRadius};
`;

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const getMatchLabel = (extractionType: ExtractionType, matchLevel: MatchLevel | null) => {
  if (extractionType === ExtractionType.Unguided) return startCase(ExtractionType.Unguided);
  if (matchLevel == null) return '';
  return `${startCase(matchLevel)} match`;
};

const getExtractionPillColor = (
  extractionType: ExtractionType,
  extractionMatchLevelSatisfied: boolean,
) =>
  extractionType === ExtractionType.Unguided
    ? ExtractionColor.Unguided
    : extractionMatchLevelSatisfied
      ? ExtractionColor.GuidedSatisfied
      : ExtractionColor.GuidedUnsatisfied;

interface ExtractionDetailWithToggleProps {
  extractionGroup: ExtractionGroup;
  focusedExtractions: FocusedExtraction[];
  setFocusedExtractions: (focusedExtractions: FocusedExtraction[]) => void;
}

export const ExtractionDetailWithToggle = ({
  extractionGroup,
  focusedExtractions,
  setFocusedExtractions,
}: ExtractionDetailWithToggleProps) => {
  const matchLabel = getMatchLabel(
    extractionGroup.extractionType,
    extractionGroup.extractionMatchLevel,
  );
  const satisfiesMatchLevel = matchLevelSatisfies(
    extractionGroup.extractionMatchLevel,
    extractionGroup.matchLevelMinimum,
  );
  const extractionPillColor = getExtractionPillColor(
    extractionGroup.extractionType,
    satisfiesMatchLevel,
  );
  const focusedExtractionMatch = focusedExtractions.find((focusedExtraction) =>
    isEqual(focusedExtraction.extractionGroup, extractionGroup),
  );
  const extractionGroupIsFocused = focusedExtractionMatch != null;

  const handleToggleDown = () =>
    setFocusedExtractions([
      {
        extractionGroup: extractionGroup,
        focusedBoundingBoxId: extractionGroupIsFocused
          ? (focusedExtractionMatch.focusedBoundingBoxId + 1) % extractionGroup.numResults
          : 1,
      },
    ]);

  const handleToggleUp = () =>
    setFocusedExtractions([
      {
        extractionGroup: extractionGroup,
        focusedBoundingBoxId: extractionGroupIsFocused
          ? (focusedExtractionMatch.focusedBoundingBoxId - 1 + extractionGroup.numResults) %
            extractionGroup.numResults
          : extractionGroup.numResults - 1,
      },
    ]);

  return (
    <Container
      id={`extraction-detail-toggle__${extractionGroup.fieldName}-${extractionGroup.fieldIdentifier}-${extractionGroup.extractionValue}`}
      onClick={() =>
        setFocusedExtractions([
          {
            extractionGroup: extractionGroup,
            focusedBoundingBoxId: 0,
          },
        ])
      }
      focused={extractionGroupIsFocused}
    >
      <TitleContainer>
        <Sticker variant={extractionPillColor}>{matchLabel}</Sticker>
        <ExtractionToggle
          index={extractionGroupIsFocused ? focusedExtractionMatch.focusedBoundingBoxId + 1 : 1}
          numTotal={extractionGroup.numResults}
          handleToggleDown={handleToggleDown}
          handleToggleUp={handleToggleUp}
        />
      </TitleContainer>
      <Spacer height={0.5} />
      {extractionGroup.fieldIdentifier && (
        <Paragraph>{`Source field: ${upperFirst(lowerCase(extractionGroup.fieldIdentifier))}`}</Paragraph>
      )}
      <Paragraph color='primary'>{extractionGroup.extractionValue || 'None'}</Paragraph>
    </Container>
  );
};

interface ExtractionDetailProps {
  extractionGroup: ExtractionGroup;
  handleClick?: () => void;
}

const ExtractionDetail = ({ extractionGroup, handleClick }: ExtractionDetailProps) => {
  const matchLabel = getMatchLabel(
    extractionGroup.extractionType,
    extractionGroup.extractionMatchLevel,
  );
  const satisfiesMatchLevel = matchLevelSatisfies(
    extractionGroup.extractionMatchLevel,
    extractionGroup.matchLevelMinimum,
  );
  const extractionPillColor = getExtractionPillColor(
    extractionGroup.extractionType,
    satisfiesMatchLevel,
  );

  return (
    <Container
      id={`extraction-detail__${extractionGroup.fieldName}-${extractionGroup.fieldIdentifier}-${extractionGroup.extractionValue}`}
      onClick={handleClick}
    >
      <TitleContainer>
        <Sticker variant={extractionPillColor}>{matchLabel}</Sticker>
      </TitleContainer>
      <Spacer height={0.5} />
      {extractionGroup.fieldIdentifier && (
        <Paragraph>{`Source field: ${upperFirst(lowerCase(extractionGroup.fieldIdentifier))}`}</Paragraph>
      )}
      <Paragraph color='primary'>{extractionGroup.extractionValue || 'None'}</Paragraph>
    </Container>
  );
};

export default ExtractionDetail;
